/**
 * Date utility functions for proper timezone handling
 *
 * All timestamps from the backend are in UTC but may not have the 'Z' suffix.
 * This utility ensures proper parsing and formatting with user timezone support.
 */

import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';

// Common timezone options for the UI
export const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European Time (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan Time (JST)' },
  { value: 'Asia/Shanghai', label: 'China Time (CST)' },
  { value: 'Asia/Kolkata', label: 'India Time (IST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'Pacific/Auckland', label: 'New Zealand (NZST)' },
];

// Local storage key for timezone preference
const TIMEZONE_STORAGE_KEY = 'user_timezone';

/**
 * Get the user's preferred timezone from localStorage or browser
 */
export function getUserTimezone(): string {
  try {
    const stored = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    if (stored && isValidTimezone(stored)) {
      return stored;
    }
  } catch {
    // localStorage not available
  }

  // Fall back to browser timezone
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * Set the user's preferred timezone
 */
export function setUserTimezone(timezone: string): void {
  try {
    if (isValidTimezone(timezone)) {
      localStorage.setItem(TIMEZONE_STORAGE_KEY, timezone);
    }
  } catch {
    // localStorage not available
  }
}

/**
 * Check if a timezone string is valid
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Parse a timestamp string as UTC
 * Backend timestamps may come without 'Z' suffix, so we need to handle that
 */
export function parseAsUTC(timestamp: string | null | undefined): Date | null {
  if (!timestamp) return null;

  try {
    let normalizedTimestamp = timestamp;

    // If the timestamp doesn't have a timezone indicator, assume UTC
    if (!timestamp.endsWith('Z') &&
        !timestamp.includes('+') &&
        !timestamp.match(/[-+]\d{2}:\d{2}$/)) {
      normalizedTimestamp = timestamp + 'Z';
    }

    const date = parseISO(normalizedTimestamp);

    // Check if valid date
    if (isNaN(date.getTime())) {
      return null;
    }

    return date;
  } catch {
    return null;
  }
}

/**
 * Format a timestamp for display in the user's timezone
 * @param timestamp - ISO timestamp string from backend
 * @param formatStr - date-fns format string
 * @param timezone - optional timezone override
 */
export function formatDateTime(
  timestamp: string | null | undefined,
  formatStr: string = 'MMM dd, yyyy HH:mm',
  timezone?: string
): string {
  const date = parseAsUTC(timestamp);
  if (!date) return 'Never';

  const tz = timezone || getUserTimezone();

  try {
    return formatInTimeZone(date, tz, formatStr);
  } catch {
    // Fallback to local format
    return format(date, formatStr);
  }
}

/**
 * Format a timestamp as relative time (e.g., "5 minutes ago")
 * @param timestamp - ISO timestamp string from backend
 * @param options - formatDistanceToNow options
 */
export function formatRelativeTime(
  timestamp: string | null | undefined,
  options?: { addSuffix?: boolean }
): string {
  const date = parseAsUTC(timestamp);
  if (!date) return 'Never';

  try {
    return formatDistanceToNow(date, { addSuffix: options?.addSuffix ?? true });
  } catch {
    return 'Unknown';
  }
}

/**
 * Format with both relative and absolute time
 * Returns an object with both values for use in tooltips
 */
export function formatTimeWithTooltip(
  timestamp: string | null | undefined,
  timezone?: string
): { relative: string; absolute: string } {
  const tz = timezone || getUserTimezone();

  return {
    relative: formatRelativeTime(timestamp),
    absolute: formatDateTime(timestamp, 'PPpp', tz), // e.g., "Jan 28, 2026, 6:00:43 AM"
  };
}

/**
 * Get the abbreviated timezone name for display
 */
export function getTimezoneAbbreviation(timezone?: string): string {
  const tz = timezone || getUserTimezone();

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    });

    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find(part => part.type === 'timeZoneName');
    return tzPart?.value || tz;
  } catch {
    return tz;
  }
}

/**
 * Convert a Date object to the user's timezone for display
 */
export function toUserTimezone(date: Date, timezone?: string): Date {
  const tz = timezone || getUserTimezone();
  return utcToZonedTime(date, tz);
}
